import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import ContactMap from '../components/ContactMap';
import SEOHead, { SITE_URL, SITE_NAME } from '../components/SEOHead';
import './Contact.css';

/* Outline-style icons for contact info */
const IconLocation = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
  </svg>
);
const IconEmail = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
  </svg>
);
const IconPhone = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
  </svg>
);

const SocialIcon = ({ name, url, children }) => {
  const className = `contact-social-circle${!url ? ' contact-social-circle-disabled' : ''}`;
  const content = <span className="contact-social-circle-icon">{children}</span>;
  if (url) {
    return (
      <a href={url} target="_blank" rel="noopener noreferrer" className={className} aria-label={name} title={name}>
        {content}
      </a>
    );
  }
  return (
    <span className={className} aria-label={name} title={name}>
      {content}
    </span>
  );
};

const DEFAULT_CONTACT = {
  contact_email: 'info@gfmstudio.com',
  contact_phone: '',
  contact_address_en: '',
  contact_address_ka: '',
  social_links: { facebook: '', instagram: '', tiktok: '', youtube: '' },
  map_lat: '41.7151',
  map_lng: '44.8271',
  visit_card_image: null,
};

const Contact = () => {
  const { i18n } = useTranslation();
  const [contactData, setContactData] = useState(DEFAULT_CONTACT);
  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    company: '',
    email: '',
    mobile: '',
    subject: '',
    body: '',
  });
  const [status, setStatus] = useState(null);
  const [errorDetail, setErrorDetail] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    const fetchContact = async () => {
      try {
        const res = await api.get('/content/contact-page/');
        setContactData(res.data);
      } catch {
        // use defaults
      }
    };
    fetchContact();
  }, []);

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
    setErrorDetail('');
    setSending(true);
    try {
      const res = await api.post('/content/contact/', form);
      if (res.status >= 200 && res.status < 300) {
        setStatus('success');
        setForm({ first_name: '', last_name: '', company: '', email: '', mobile: '', subject: '', body: '' });
      } else {
        setErrorDetail(res?.data?.error || '');
        setStatus('error');
      }
    } catch (err) {
      const apiError = err?.response?.data?.error;
      setErrorDetail(apiError || err.message || '');
      setStatus('error');
    } finally {
      setSending(false);
    }
  };

  const { contact_email, contact_phone, contact_address_en, contact_address_ka, social_links, map_lat, map_lng } = contactData;
  const contact_address = i18n.language === 'ka' ? (contact_address_ka || contact_address_en) : (contact_address_en || contact_address_ka);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Contact GFM Studio',
    url: `${SITE_URL}/contact`,
    about: {
      '@type': 'Organization',
      name: SITE_NAME,
      url: SITE_URL,
      ...(contact_email && { email: contact_email }),
      ...(contact_phone && { telephone: contact_phone }),
      ...(contact_address && { address: contact_address }),
    },
  };

  return (
    <div className="contact-page contact-page-reference">
      <SEOHead
        title="Contact GFM Studio | Get a Quote"
        description="Get in touch with GFM Studio for live production, streaming, and event broadcast inquiries."
        path="/contact"
        jsonLd={jsonLd}
      />
      <div className="contact-header">
        <h1>{i18n.language === 'ka' ? 'კონტაქტი' : 'Contact Us'}</h1>
        <span className="contact-header-underline" />
      </div>

      <div className="contact-content contact-content-split">
        {/* Left column: contact info + map stacked, same width */}
        <div className="contact-left-column">
          <div className="contact-card contact-card-social">
            <section className="contact-get-in-touch">
              <h2>{i18n.language === 'ka' ? 'საკონტაქტო ინფორმაცია' : 'Contact Information'}</h2>
              <span className="contact-section-underline" />
              <div className="contact-info-list">
                <div className="contact-info-item">
                  <span className="contact-info-icon"><IconPhone /></span>
                  {contact_phone ? (
                    <a href={`tel:${contact_phone}`} className="contact-info-link">{contact_phone}</a>
                  ) : (
                    <span className="contact-info-muted">—</span>
                  )}
                </div>
                <a href={`mailto:${contact_email}`} className="contact-info-item contact-info-link">
                  <span className="contact-info-icon"><IconEmail /></span>
                  <span>{contact_email}</span>
                </a>
                <div className="contact-info-item">
                  <span className="contact-info-icon"><IconLocation /></span>
                  {contact_address ? (
                    <span>{contact_address}</span>
                  ) : (
                    <span className="contact-info-muted">—</span>
                  )}
                </div>
                <div className="contact-follow-us">
                  <span className="contact-follow-label">{i18n.language === 'ka' ? 'გამოგვყევით:' : 'Follow Us:'}</span>
                  <div className="contact-social-circles">
                    <SocialIcon name="Facebook" url={social_links?.facebook}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
              </svg>
                    </SocialIcon>
                    <SocialIcon name="Instagram" url={social_links?.instagram}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
              </svg>
                    </SocialIcon>
                    <SocialIcon name="TikTok" url={social_links?.tiktok}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
              </svg>
                    </SocialIcon>
                    <SocialIcon name="YouTube" url={social_links?.youtube}>
              <svg viewBox="0 0 24 24" fill="currentColor" width="18" height="18">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
              </svg>
                    </SocialIcon>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Map moved up into left column, directly under contact card */}
          <div className="contact-card contact-card-map">
            <h2 className="contact-map-title">{i18n.language === 'ka' ? 'ჩვენი მისამართი' : 'Our location'}</h2>
            <span className="contact-section-underline" />
            <div className="contact-map-wrapper">
              <ContactMap lat={map_lat} lng={map_lng} />
            </div>
          </div>
        </div>

        {/* Right: Contact form */}
        <div className="contact-card contact-card-form">
        <section className="contact-form-section">
          <h2>{i18n.language === 'ka' ? 'შეტყობინების გაგზავნა' : 'Send a message'}</h2>
          <span className="contact-section-underline" />
          <form className="contact-form" onSubmit={handleSubmit}>
            <div className="contact-form-row contact-form-row-3">
              <div className="contact-form-field">
                <label htmlFor="first_name">{i18n.language === 'ka' ? 'სახელი' : 'First name'}</label>
                <input
                  id="first_name"
                  name="first_name"
                  type="text"
                  required
                  placeholder={i18n.language === 'ka' ? 'სახელი' : 'First name'}
                  value={form.first_name}
                  onChange={handleChange}
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="last_name">{i18n.language === 'ka' ? 'გვარი' : 'Last name'}</label>
                <input
                  id="last_name"
                  name="last_name"
                  type="text"
                  required
                  placeholder={i18n.language === 'ka' ? 'გვარი' : 'Last name'}
                  value={form.last_name}
                  onChange={handleChange}
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="company">{i18n.language === 'ka' ? 'კომპანია' : 'Company'}</label>
                <input
                  id="company"
                  name="company"
                  type="text"
                  placeholder={i18n.language === 'ka' ? 'კომპანია' : 'Company'}
                  value={form.company}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="contact-form-row">
              <div className="contact-form-field">
                <label htmlFor="email">{i18n.language === 'ka' ? 'ელ. ფოსტა' : 'Email'}</label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  required
                  placeholder={i18n.language === 'ka' ? 'ელ. ფოსტა' : 'Email address'}
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              <div className="contact-form-field">
                <label htmlFor="mobile">{i18n.language === 'ka' ? 'მობილური' : 'Mobile number'}</label>
                <input
                  id="mobile"
                  name="mobile"
                  type="tel"
                  placeholder={i18n.language === 'ka' ? 'მობილური' : 'Mobile number'}
                  value={form.mobile}
                  onChange={handleChange}
                />
              </div>
            </div>
            <div className="contact-form-field contact-form-field-full">
              <label htmlFor="subject">{i18n.language === 'ka' ? 'თემა' : 'Subject'}</label>
              <input
                id="subject"
                name="subject"
                type="text"
                required
                placeholder={i18n.language === 'ka' ? 'თემა' : 'Mail subject'}
                value={form.subject}
                onChange={handleChange}
              />
            </div>
            <div className="contact-form-field contact-form-field-full">
              <label htmlFor="body">{i18n.language === 'ka' ? 'შეტყობინება' : 'Message'}</label>
              <textarea
                id="body"
                name="body"
                rows={8}
                required
                placeholder={i18n.language === 'ka' ? 'შეტყობინების ტექსტი' : 'Mail text body'}
                value={form.body}
                onChange={handleChange}
                className="contact-form-textarea-large"
              />
            </div>
            <button type="submit" className="contact-form-submit contact-form-submit-full" disabled={sending}>
              {sending ? (i18n.language === 'ka' ? 'იგზავნება...' : 'Sending...') : (i18n.language === 'ka' ? 'გაგზავნა' : 'Send Message')}
            </button>
            {status === 'success' && (
              <p className="contact-form-status success">{i18n.language === 'ka' ? 'გმადლობთ! თქვენი შეტყობინება გაიგზავნა.' : 'Thank you! Your message has been sent.'}</p>
            )}
            {status === 'error' && (
              <p className="contact-form-status error">
                {i18n.language === 'ka' ? 'შეცდომა. გთხოვთ სცადოთ მოგვიანებით.' : 'Something went wrong. Please try again later.'}
                {errorDetail && <span style={{ display: 'block', marginTop: 8, fontSize: '0.9em' }}>{errorDetail}</span>}
              </p>
            )}
          </form>
        </section>
        </div>
      </div>
    </div>
  );
};

export default Contact;
